import { ApartmentOutlined, UserOutlined } from '@ant-design/icons';
import { Col, Popover, Radio, RadioChangeEvent, Row, Typography } from 'antd';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import useTicketByKey from '../hooks/ticketDetailPage/useTicketByKey';
import useToggle from '../hooks/useToggle';
import useUserById, { deriveCustomizedDetailedUser } from '../hooks/useUserById';
import { BreadCrumbsViewState } from '../layouts/Pages/TicketPagesContentLayout/TicketDetailContentLayout';
import { DetailedUser, TicketSystemFieldsKey, roles } from '../model/types';
import { getSafeTicketKeyFromUrl, getUserIdFromUrl } from '../store/childStore/tabStore';
import {
    getTicketsPageUserRedirectUrlString,
    getUsersPageUserRedirectUrlString,
    isCustomerPage,
    isOrganizationPage,
    isTicketsPage
} from '../utils/routeHelper';
import { getTicketKey, getTicketKeyNumberPart, getTicketStatus } from '../utils/ticketHelper';
import MiniTag from './MiniTag';
import OrganizationCreateModal from './OrganizationDetailPage/OrganizationCreateModal';

const { Group, Button } = Radio;

interface BreadCrumbsProps {
    viewOption: BreadCrumbsViewState;
}

const resolveRelatedTicketDetailPage: (url: string) => string = (url) => {
    const ticketKey = getSafeTicketKeyFromUrl(url);
    return `/tickets/${ticketKey}`;
};

const resolveRelatedRequesterTicketsPage: (url: string, role: roles) => string = (url, role) => {
    const ticketKey = getSafeTicketKeyFromUrl(url);
    return getTicketsPageUserRedirectUrlString(ticketKey, role);
};
export const resolveRelatedOrganizationTicketsPage: (url: string) => string = (url) => {
    const ticketKey = getSafeTicketKeyFromUrl(url);
    return `/tickets/${ticketKey}/organization/tickets?page=1&size=5`;
};

const resolveRelatedCustomerDetailPage: (url: string, role: roles) => string = (url, role) => {
    const customerKey = getUserIdFromUrl(url);
    return getUsersPageUserRedirectUrlString(customerKey, role);
};
export const resolveRelatedCustomerOrganizationPage: (url: string) => string = (url) => {
    const customerKey = getUserIdFromUrl(url);
    return `/users/${customerKey}/organization/tickets?page=1&size=5`;
};

export default function BreadCrumbs({ viewOption }: BreadCrumbsProps) {
    const {
        asPath,
        query: { ticketKey, userId, organizationId },
        push
    } = useRouter();

    const { t } = useTranslation('common');
    const [isOrganizationModalOpen, , openOrganizationModal, closeOrganizationModal] = useToggle(false);

    const { data: ticketDetail } = useTicketByKey(ticketKey?.toString());
    const requesterId =
        userId?.toString() ??
        (ticketDetail?.fieldMap?.[TicketSystemFieldsKey.requester]?.value as DetailedUser)?.id?.toString();
    const { data, error } = useUserById(requesterId);
    const isUSerDeleted = (error as AxiosError<Error>)?.response?.statusText === 'USER_NOT_FOUND';
    const userInfo = deriveCustomizedDetailedUser(data);

    const orgId = organizationId?.toString() ?? userInfo?.organization?.id?.toString();

    const handleOnChange = ({ target: { value } }: RadioChangeEvent) => {
        if (!orgId && value === BreadCrumbsViewState.organizationView) {
            openOrganizationModal();
            return;
        }

        let targetUrl: string;
        if (isCustomerPage(asPath)) {
            if (value === BreadCrumbsViewState.userView) {
                targetUrl = resolveRelatedCustomerDetailPage(asPath, userInfo?.role?.authority);
            }
            if (value === BreadCrumbsViewState.organizationView) {
                targetUrl = resolveRelatedCustomerOrganizationPage(asPath);
            }
        }
        if (isTicketsPage(asPath)) {
            if (value === BreadCrumbsViewState.ticketView) {
                targetUrl = resolveRelatedTicketDetailPage(asPath);
            }
            if (value === BreadCrumbsViewState.userView) {
                targetUrl = resolveRelatedRequesterTicketsPage(asPath, userInfo?.role?.authority);
            }
            if (value === BreadCrumbsViewState.organizationView) {
                targetUrl = resolveRelatedOrganizationTicketsPage(asPath);
            }
        }

        targetUrl && push(targetUrl);
    };

    const isEditMode = ticketKey ? true : false;

    const ticketKeyText = getTicketKey(ticketDetail)
        ? `${t('ticket')} #${getTicketKeyNumberPart(ticketDetail)}`
        : t('ticket');

    return (
        !isOrganizationPage(asPath) && (
            <>
                <Group className="full-width" onChange={handleOnChange} size="small" value={viewOption}>
                    {(isCustomerPage(asPath) || isEditMode) && (
                        <>
                            <Popover
                                title={t('deletedUserPopoverTitle', { userId: requesterId })}
                                trigger={isUSerDeleted ? 'hover' : []}
                            >
                                <Button
                                    disabled={isUSerDeleted}
                                    style={{ width: ticketKey ? '25%' : '50%' }}
                                    value={BreadCrumbsViewState.organizationView}
                                    onClick={() => {
                                        !orgId && openOrganizationModal();
                                    }}
                                >
                                    <Row align="middle" justify="center">
                                        <Col>{<ApartmentOutlined />}</Col>
                                    </Row>
                                </Button>
                                <Button
                                    disabled={isUSerDeleted}
                                    style={{ width: ticketKey ? '25%' : '50%' }}
                                    value={BreadCrumbsViewState.userView}
                                >
                                    <Row align="middle" justify="center">
                                        <Col>{<UserOutlined />}</Col>
                                    </Row>
                                </Button>
                            </Popover>
                        </>
                    )}
                    {!isCustomerPage(asPath) && (
                        <Button style={{ width: !isEditMode ? '100%' : '50%' }} value={BreadCrumbsViewState.ticketView}>
                            <Row align="middle" justify="center" gutter={5}>
                                <Col {...(getTicketKeyNumberPart(ticketDetail) > 100000000 ? { span: 3 } : {})}>
                                    <MiniTag mini status={getTicketStatus(ticketDetail) ?? 'New'} />
                                </Col>
                                <Col {...(getTicketKeyNumberPart(ticketDetail) > 100000000 ? { span: 20 } : {})}>
                                    {viewOption === BreadCrumbsViewState.ticketView ? (
                                        <Typography.Link ellipsis>{ticketKeyText}</Typography.Link>
                                    ) : (
                                        <Typography.Text type="secondary" ellipsis>
                                            {ticketKeyText}
                                        </Typography.Text>
                                    )}
                                </Col>
                            </Row>
                        </Button>
                    )}
                </Group>

                <OrganizationCreateModal
                    userId={requesterId}
                    isOpen={isOrganizationModalOpen}
                    closeOrganizationModal={closeOrganizationModal}
                />
            </>
        )
    );
}
